/**
 * Hook for handling drag-and-drop file uploads to project folders.
 * Supports all file types - text files are read as UTF-8 text,
 * binary files (images, PDFs, etc.) are read as base64.
 * 
 * Files are stored as drafts and can be saved using the existing
 * save functionality in the code editor.
 */

import { useCallback, useState } from "react";
import { toast } from "sonner";
import type { DragEvent } from "react";
import { isReadOnlyPath } from "@/lib/read-only-files";

// Maximum file size: 10 MB for any file
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const ROOT_LABEL = "project root";

// Text file extensions that should be read as text instead of base64
const TEXT_FILE_EXTENSIONS = new Set([
  "txt",
  "md",
  "json",
  "js",
  "jsx",
  "ts",
  "tsx",
  "css",
  "scss",
  "sass",
  "less",
  "html",
  "htm",
  "xml",
  "svg",
  "yaml",
  "yml",
  "toml",
  "ini",
  "env",
  "gitignore",
  "sh",
  "bash",
  "zsh",
  "fish",
  "py",
  "rb",
  "go",
  "rs",
  "java",
  "c",
  "cpp",
  "h",
  "hpp",
  "cs",
  "php",
  "sql",
  "graphql",
  "vue",
  "svelte",
  "astro",
  "prisma",
  "proto",
]);

// Binary/image file extensions that should be read as base64
const BINARY_FILE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "ico",
  "svg",
  "pdf",
  "zip",
  "tar",
  "gz",
  "woff",
  "woff2",
  "ttf",
  "otf",
  "eot",
  "mp3",
  "mp4",
  "wav",
  "avi",
  "mov",
  "webm",
]);

type SetDraftFn = (
  path: string,
  content: string,
  options?: { createIfNotExists?: boolean },
) => void;

type UseImageFolderDropParams = {
  serverFilePaths: Set<string>;
  setDraft: SetDraftFn;
  setSelectedFile: (path: string | null) => void;
};

type UseImageFolderDropReturn = {
  draggedDirectory: string | null;
  handleDirectoryDragOver: (
    event: DragEvent<HTMLDivElement>,
    path: string,
  ) => void;
  handleDirectoryDragLeave: (
    event: DragEvent<HTMLDivElement>,
    path: string,
  ) => void;
  handleDirectoryDrop: (event: DragEvent<HTMLDivElement>, path: string) => void;
  handleRootDragOver: (event: DragEvent<HTMLDivElement>) => void;
  handleRootDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  handleRootDrop: (event: DragEvent<HTMLDivElement>) => void;
  isRootDragTarget: boolean;
};

function getFileExtension(fileName: string): string | null {
  const parts = fileName.split(".");
  if (parts.length < 2) return null;
  return parts[parts.length - 1]?.toLowerCase() ?? null;
}

function shouldReadAsText(file: File): boolean {
  // Check if file type suggests text
  if (file.type.startsWith("text/")) {
    return true;
  }
  
  // Check file extension
  const extension = getFileExtension(file.name);
  if (!extension) {
    // No extension - default to text for small files, binary for larger
    return file.size < 1024 * 100; // 100KB threshold
  }
  
  return TEXT_FILE_EXTENSIONS.has(extension);
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Unable to read file"));
        return;
      }
      const separatorIndex = result.indexOf(",");
      if (separatorIndex === -1) {
        reject(new Error("Malformed file data"));
        return;
      }
      resolve(result.slice(separatorIndex + 1));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("Failed to read file"));
    };
    reader.readAsDataURL(file);
  });
}

async function fileToText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Unable to read file as text"));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("Failed to read file"));
    };
    reader.readAsText(file);
  });
}

async function readFileContent(file: File): Promise<string> {
  if (shouldReadAsText(file)) {
    return fileToText(file);
  }
  return fileToBase64(file);
}

function isFileDragEvent(event: DragEvent<HTMLElement>): boolean {
  return (
    !!event.dataTransfer &&
    Array.from(event.dataTransfer.items || []).some(
      (item) => item.kind === "file",
    )
  );
}

export function useImageFolderDrop({
  serverFilePaths,
  setDraft,
  setSelectedFile,
}: UseImageFolderDropParams): UseImageFolderDropReturn {
  const [draggedDirectory, setDraggedDirectory] = useState<string | null>(null);

  const handleDirectoryDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>, path: string) => {
      if (!isFileDragEvent(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setDraggedDirectory(path);
    },
    [],
  );

  const handleDirectoryDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>, path: string) => {
      const relatedTarget = event.relatedTarget as Node | null;
      if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
        return;
      }
      setDraggedDirectory((current) => (current === path ? null : current));
    },
    [],
  );

  const handleDirectoryDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>, path: string) => {
      if (!isFileDragEvent(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setDraggedDirectory(null);

      const droppedFiles = event.dataTransfer?.files;
      if (!droppedFiles || droppedFiles.length === 0) {
        return;
      }

      const filesArray = Array.from(droppedFiles);

      // Check for oversized files
      const oversizedFiles = filesArray.filter(
        (file) => file.size > MAX_FILE_SIZE_BYTES,
      );
      if (oversizedFiles.length) {
        const fileList =
          oversizedFiles.length === 1
            ? oversizedFiles[0]?.name
            : `${oversizedFiles.length} files`;
        toast.error(`File(s) too large: ${fileList}. Maximum size is 10 MB.`);
        return;
      }

      const addedFiles: Array<string> = [];
      let readOnlySkipped = 0;
      let readFailures = 0;
      let lastPath: string | null = null;

      for (const file of filesArray) {
        const targetPath = path ? `${path}/${file.name}` : file.name;
        if (isReadOnlyPath(targetPath)) {
          readOnlySkipped += 1;
          continue;
        }

        try {
          const content = await readFileContent(file);
          setDraft(targetPath, content, {
            createIfNotExists: !serverFilePaths.has(targetPath),
          });
          addedFiles.push(file.name);
          lastPath = targetPath;
        } catch (error) {
          console.error("[imagine] Failed to read dropped file", error);
          readFailures += 1;
        }
      }

      if (lastPath) {
        setSelectedFile(lastPath);
      }

      if (addedFiles.length) {
        const folderLabel = path || ROOT_LABEL;
        const message =
          addedFiles.length === 1
            ? `Added ${addedFiles[0]} to ${folderLabel}`
            : `Added ${addedFiles.length} files to ${folderLabel}`;
        toast.success(message);
      }

      if (readOnlySkipped || readFailures) {
        const reasons: Array<string> = [];
        if (readOnlySkipped) {
          reasons.push(
            readOnlySkipped === 1
              ? "read-only path"
              : `${readOnlySkipped} read-only paths`,
          );
        }
        if (readFailures) {
          reasons.push(
            readFailures === 1 ? "read error" : `${readFailures} read errors`,
          );
        }
        const summary = reasons.join(", ");
        toast.error(`Skipped files (${summary})`);
      }
    },
    [serverFilePaths, setDraft, setSelectedFile],
  );

  const handleRootDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      handleDirectoryDragOver(event, "");
    },
    [handleDirectoryDragOver],
  );

  const handleRootDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      handleDirectoryDragLeave(event, "");
    },
    [handleDirectoryDragLeave],
  );

  const handleRootDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      handleDirectoryDrop(event, "");
    },
    [handleDirectoryDrop],
  );

  return {
    draggedDirectory,
    handleDirectoryDragOver,
    handleDirectoryDragLeave,
    handleDirectoryDrop,
    handleRootDragOver,
    handleRootDragLeave,
    handleRootDrop,
    isRootDragTarget: draggedDirectory === "",
  };
}
